/**
 * Heat plan vs master consumption maths, shared by the voyage comparison page and the
 * home page voyage overview so both show the same figures.
 */

/** One day of a heat plan (get_logs_of_heatplan). Only the fields the consumption maths needs. */
export interface HeatplanConsumptionRow {
  date: string;
  overall_consumption?: number | null;
  bunker_raise?: number | null;
  bunker_maintain?: number | null;
  bunker_dhe?: number | null;
  bunker_ae?: number | null;
  bunker_pipeline_loss?: number | null;
}

/** One master log row (voyage_logs). Only the fields the consumption maths needs. */
export interface MasterConsumptionRow {
  record_date?: string;
  bc_total_hfo_mt?: string | number | null;
  bc_total_mgo_mt?: string | number | null;
}

export interface RaiseMaintainSplit {
  raise: number;
  maintain: number;
}

export interface VoyageConsumptionSummary {
  /** Every master log of the voyage (HFO + MGO). */
  masterTotalMt: number;
  /** Master total split in the heat plan's raise : maintain ratio; null when the plan has no split. */
  masterSplit: RaiseMaintainSplit | null;
  /** Master logs dated on a heat plan day only (the comparison page's "Master · heat plan days"). */
  masterPlanDaysMt: number;
  /** masterPlanDaysMt split in the heat plan's raise : maintain ratio; null when the plan has no split. */
  masterPlanDaysSplit: RaiseMaintainSplit | null;
  /** Plan days the master has logged. */
  masterReportedPlanDays: number;
  /** Planned consumption up to (and including) the last master-reported plan day. */
  planTillLastReportMt: number;
  /** Planned raise / maintain over the same days as planTillLastReportMt. */
  planSplit: RaiseMaintainSplit;
  /** Planned consumption over every day of the merged plans. */
  planFullMt: number;
  /** Last plan day the master has reported; null when the master has not reported any plan day. */
  lastMasterPlanDate: string | null;
  planDays: number;
}

const toNumber = (value: unknown): number => {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
};

/** Raw get_logs_of_heatplan body → its logs, sorted by date. */
export function extractHeatplanLogs<T extends { date?: string }>(body: any): T[] {
  const payload = body?.response && typeof body.response === 'object' ? body.response : body;
  return Array.isArray(payload?.logs)
    ? [...payload.logs].sort((a, b) => String(a?.date || '').localeCompare(String(b?.date || '')))
    : [];
}

/** Raw get_combined_voyage_data body → its master log rows. */
export function extractMasterVoyageLogs<T = MasterConsumptionRow>(body: any): T[] {
  if (Array.isArray(body)) {
    return body;
  }
  const logs = body?.voyage_logs ?? body?.response?.voyage_logs ?? body?.data?.voyage_logs;
  return Array.isArray(logs) ? logs : [];
}

/**
 * Merges the logs of several heat plans into one row per date. Plans go in ascending plan order and a
 * later plan overwrites the days it shares with the previous one: a new plan is sent every 5 days for
 * the next 7, so its first two days replace the previous plan's last two.
 */
export function mergeHeatplanLogsByDate<T extends { date?: string }>(planLogsList: T[][]): T[] {
  const byDate = new Map<string, T>();
  planLogsList.forEach((planLogs) => {
    (planLogs || []).forEach((row) => {
      if (row && row.date) {
        byDate.set(row.date, row);
      }
    });
  });
  return [...byDate.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([, row]) => row);
}

/** Planned raise (bunker_raise) and maintain (bunker_maintain + DHE + AE + pipeline loss). */
export function sumPlanRaiseMaintain(rows: HeatplanConsumptionRow[]): RaiseMaintainSplit {
  return rows.reduce<RaiseMaintainSplit>(
    (acc, row) => ({
      raise: acc.raise + toNumber(row.bunker_raise),
      maintain:
        acc.maintain +
        toNumber(row.bunker_maintain) +
        toNumber(row.bunker_dhe) +
        toNumber(row.bunker_ae) +
        toNumber(row.bunker_pipeline_loss),
    }),
    { raise: 0, maintain: 0 },
  );
}

/** Raise share (0–1) of a planned split; null when the plan has neither raise nor maintain. */
export function raiseShareOf(split: RaiseMaintainSplit): number | null {
  const total = split.raise + split.maintain;
  return total > 0 ? split.raise / total : null;
}

/** Splits a total in the given raise share; maintain takes the remainder so both add back up. */
export function splitByRaiseShare(total: number, share: number | null): RaiseMaintainSplit | null {
  if (share == null) {
    return null;
  }
  const raise = total * share;
  return { raise, maintain: total - raise };
}

export function masterLogConsumptionMt(row: MasterConsumptionRow): number {
  return toNumber(row.bc_total_hfo_mt) + toNumber(row.bc_total_mgo_mt);
}

/**
 * Whole-voyage summary from the merged heat plan rows and the master logs: the same figures as the
 * comparison page's "Master reported · voyage" and "Heat plan" tiles with every plan selected.
 */
export function summariseVoyageConsumption(
  planRows: HeatplanConsumptionRow[],
  masterRows: MasterConsumptionRow[],
): VoyageConsumptionSummary {
  const masterDates = new Set(masterRows.map((row) => row.record_date).filter(Boolean));
  const planDates = planRows.map((row) => row.date).filter(Boolean).sort();
  const lastMasterPlanDate = [...planDates].reverse().find((date) => masterDates.has(date)) ?? null;
  const reportedRows = lastMasterPlanDate
    ? planRows.filter((row) => row.date && row.date <= lastMasterPlanDate)
    : [];

  const planDateSet = new Set(planDates);
  const masterTotalMt = masterRows.reduce((sum, row) => sum + masterLogConsumptionMt(row), 0);
  const masterPlanDaysMt = masterRows
    .filter((row) => row.record_date && planDateSet.has(row.record_date))
    .reduce((sum, row) => sum + masterLogConsumptionMt(row), 0);
  const planSplit = sumPlanRaiseMaintain(reportedRows);
  const raiseShare = raiseShareOf(planSplit);

  return {
    masterTotalMt,
    masterSplit: splitByRaiseShare(masterTotalMt, raiseShare),
    masterPlanDaysMt,
    masterPlanDaysSplit: splitByRaiseShare(masterPlanDaysMt, raiseShare),
    masterReportedPlanDays: planDates.filter((date) => masterDates.has(date)).length,
    planTillLastReportMt: reportedRows.reduce((sum, row) => sum + toNumber(row.overall_consumption), 0),
    planSplit,
    planFullMt: planRows.reduce((sum, row) => sum + toNumber(row.overall_consumption), 0),
    lastMasterPlanDate,
    planDays: planDates.length,
  };
}
